// Per-tenant Analytics tab body. Rendered from TenantDetail when
// activeTab === 'analytics'. Mirrors the global page (cards with delta vs
// prior period, trend line chart, range bar) plus a last-activity caption
// that answers "is this tenant still using the product?" at a glance.
//
// The stat cards double as the chart's metric selector — unlike the global
// page, active_customers IS chartable here (no leaderboard coupling; the
// daily snapshot reads naturally as a line).

import { useState } from 'react'

import { useTenantAnalytics, type DailyRow } from '../hooks/useAnalytics.js'
import { AdminApiError } from '../adminApi.js'
import {
  AnalyticsRangeBar,
  defaultRange,
  rangeSpanDays,
  type RangePreset,
} from './AnalyticsRangeBar.js'
import { AnalyticsStatCard } from './AnalyticsStatCard.js'
import { TrendChart } from '../../shared/trend-chart/TrendChart.js'

interface Props {
  tenantId: string
}

type ChartMetric = keyof Omit<DailyRow, 'date'>

const METRIC_CHART_OPTIONS: Array<{
  key: ChartMetric
  label: string
  unit: string
  /** Snapshot metrics get a static sub-line instead of a delta. */
  sub?: string
}> = [
  { key: 'passes_installed', label: 'Passes installed', unit: 'passes' },
  { key: 'stamps_issued', label: 'Stamps issued', unit: 'stamps' },
  { key: 'rewards_redeemed', label: 'Rewards redeemed', unit: 'rewards' },
  { key: 'cycle_completions', label: 'Cycle completions', unit: 'cycles' },
  { key: 'broadcasts_sent', label: 'Broadcasts sent', unit: 'broadcasts' },
  {
    key: 'active_customers',
    label: 'Active customers',
    unit: 'customers',
    sub: 'last 30 days',
  },
]

export function TenantAnalytics({ tenantId }: Props) {
  const [range, setRange] = useState(defaultRange())
  const [chartMetric, setChartMetric] = useState<ChartMetric>('stamps_issued')

  const q = useTenantAnalytics(tenantId, range.from, range.to)

  function handleRangeChange(from: string, to: string, preset: RangePreset) {
    setRange({ from, to, preset })
  }

  if (q.isLoading) {
    return <p className="admin-audit-meta">Loading analytics…</p>
  }
  if (q.isError) {
    const msg =
      q.error instanceof AdminApiError
        ? q.error.userMessage
        : "Couldn't load analytics."
    return <p className="admin-audit-meta">{msg}</p>
  }
  if (!q.data) return null

  const { totals, previous_totals, daily, last_refreshed_at, last_activity } =
    q.data
  const activeMetric = METRIC_CHART_OPTIONS.find((m) => m.key === chartMetric)!
  const chartData = daily.map((d) => ({
    date: d.date,
    value: d[chartMetric],
  }))
  const spanDays = rangeSpanDays(range.from, range.to)

  return (
    <div className="admin-section">
      <AnalyticsRangeBar
        from={range.from}
        to={range.to}
        preset={range.preset}
        onChange={handleRangeChange}
        lastRefreshedAt={last_refreshed_at}
        loading={q.isFetching}
      />

      <p className="admin-audit-meta admin-last-activity">
        Last stamp:{' '}
        <strong>{formatPhTimestamp(last_activity.last_stamp_at)}</strong>
        {' · '}
        Last broadcast:{' '}
        <strong>{formatPhTimestamp(last_activity.last_broadcast_at)}</strong>
      </p>

      <div className="admin-stat-grid">
        {METRIC_CHART_OPTIONS.map((m) => (
          <AnalyticsStatCard
            key={m.key}
            label={m.label}
            value={totals[m.key]}
            prev={m.sub ? undefined : previous_totals[m.key]}
            prevSpanDays={m.sub ? undefined : spanDays}
            sub={m.sub}
            selected={chartMetric === m.key}
            onSelect={() => setChartMetric(m.key)}
          />
        ))}
      </div>

      <div className="admin-chart" style={{ marginTop: 18 }}>
        <div className="admin-chart-title">{activeMetric.label}</div>
        <TrendChart data={chartData} unit={activeMetric.unit} />
        {!chartData.some((d) => d.value > 0) && (
          <p className="admin-chart-empty">
            No {activeMetric.unit} recorded in this range yet.
          </p>
        )}
      </div>
    </div>
  )
}

function formatPhTimestamp(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-PH', {
    timeZone: 'Asia/Manila',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}
